import { useState, useCallback, useRef } from "react";
import {
  Image as ImageIcon,
  SlidersHorizontal,
  Plus,
  Pencil,
  Trash2,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { toast } from "sonner";
import { Button, Input, Label, Switch } from "@cieda/djayma-ui";
import { MarketingImageEditor } from "@/components/admin/images";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { useLocalCrud, generateId, LS_KEYS } from "./cms-store";
import { ConfirmDialog } from "./ConfirmDialog";
import { OfflineBadge } from "./OfflineBadge";
import type { HeroSlide, HeroSlideFormData } from "./cms-types";

// ---------------------------------------------------------------------------
// Empty form default
// ---------------------------------------------------------------------------

const EMPTY_SLIDE_FORM: HeroSlideFormData = {
  title: "",
  subtitle: "",
  image_url: "",
  cta_text: "",
  cta_link: "",
  sort_order: 1,
  active: true,
};

// ---------------------------------------------------------------------------
// Slide form modal
// ---------------------------------------------------------------------------

function HeroSlideFormModal({
  open,
  onClose,
  onSubmit,
  initialData,
  isSubmitting,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (data: HeroSlideFormData) => void;
  readonly initialData: HeroSlideFormData;
  readonly isSubmitting: boolean;
}) {
  const [form, setForm] = useState<HeroSlideFormData>(initialData);

  const prevDataRef = useRef(initialData);
  if (prevDataRef.current !== initialData) {
    prevDataRef.current = initialData;
    setForm(initialData);
  }

  const updateField = <K extends keyof HeroSlideFormData>(
    key: K,
    value: HeroSlideFormData[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Le titre est obligatoire");
      return;
    }
    onSubmit(form);
  };

  const isEditing = initialData.title !== "";

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title={isEditing ? "Modifier la slide" : "Ajouter une slide"}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting
              ? "Chargement..."
              : isEditing
                ? "Mettre \u00E0 jour"
                : "Cr\u00E9er"}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="slide-title">Titre *</Label>
          <Input
            id="slide-title"
            value={form.title}
            onChange={(e) => updateField("title", e.target.value)}
            placeholder="Titre de la slide"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="slide-subtitle">Sous-titre</Label>
          <Input
            id="slide-subtitle"
            value={form.subtitle}
            onChange={(e) => updateField("subtitle", e.target.value)}
            placeholder="Sous-titre (optionnel)"
          />
        </div>

        <MarketingImageEditor
          preset="hero"
          value={form.image_url}
          onChange={(url) => updateField("image_url", url)}
          label="Image de la slide"
        />

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="slide-cta-text">Texte du bouton (CTA)</Label>
            <Input
              id="slide-cta-text"
              value={form.cta_text}
              onChange={(e) => updateField("cta_text", e.target.value)}
              placeholder="Découvrir"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="slide-cta-link">Lien du bouton</Label>
            <Input
              id="slide-cta-link"
              value={form.cta_link}
              onChange={(e) => updateField("cta_link", e.target.value)}
              placeholder="/produits"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="slide-order">Ordre d'affichage</Label>
            <Input
              id="slide-order"
              type="number"
              min={1}
              value={form.sort_order}
              onChange={(e) =>
                updateField("sort_order", parseInt(e.target.value, 10) || 1)
              }
            />
          </div>
          <div className="flex items-end gap-3 pb-0.5">
            <Switch
              id="slide-active"
              checked={form.active}
              onCheckedChange={(val) => updateField("active", val)}
            />
            <Label htmlFor="slide-active">Actif</Label>
          </div>
        </div>
      </form>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Hero Slides Tab
// ---------------------------------------------------------------------------

export function HeroSlidesTab() {
  const crud = useLocalCrud<HeroSlide>({
    queryKey: "cms-hero-slides",
    apiPath: "/api/admin/cms/hero-slides",
    lsKey: LS_KEYS.heroSlides,
    extractKey: "slides",
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editingSlide, setEditingSlide] = useState<HeroSlide | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HeroSlide | null>(null);

  const openCreateModal = useCallback(() => {
    setEditingSlide(null);
    setModalOpen(true);
  }, []);

  const openEditModal = useCallback((slide: HeroSlide) => {
    setEditingSlide(slide);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingSlide(null);
  }, []);

  const handleSubmit = useCallback(
    (data: HeroSlideFormData) => {
      if (editingSlide) {
        crud.updateItem({
          ...editingSlide,
          title: data.title,
          subtitle: data.subtitle || null,
          image_url: data.image_url || null,
          cta_text: data.cta_text || null,
          cta_link: data.cta_link || null,
          sort_order: data.sort_order,
          active: data.active,
        });
      } else {
        crud.createItem({
          id: generateId(),
          title: data.title,
          subtitle: data.subtitle || null,
          image_url: data.image_url || null,
          cta_text: data.cta_text || null,
          cta_link: data.cta_link || null,
          sort_order: data.sort_order,
          active: data.active,
          created_at: new Date().toISOString(),
        });
      }
      closeModal();
    },
    [editingSlide, crud, closeModal],
  );

  const handleToggleActive = useCallback(
    (slide: HeroSlide) => {
      crud.updateItem({ ...slide, active: !slide.active });
    },
    [crud],
  );

  const handleMoveUp = useCallback(
    (slide: HeroSlide) => {
      const sorted = [...crud.items].sort(
        (a, b) => a.sort_order - b.sort_order,
      );
      const idx = sorted.findIndex((s) => s.id === slide.id);
      if (idx <= 0) return;
      const reordered = [...sorted];
      const prev = reordered[idx - 1];
      reordered[idx - 1] = { ...slide, sort_order: prev.sort_order };
      reordered[idx] = { ...prev, sort_order: slide.sort_order };
      crud.reorderItems(
        reordered.map((s, i) => ({ ...s, sort_order: i + 1 })),
      );
    },
    [crud],
  );

  const handleMoveDown = useCallback(
    (slide: HeroSlide) => {
      const sorted = [...crud.items].sort(
        (a, b) => a.sort_order - b.sort_order,
      );
      const idx = sorted.findIndex((s) => s.id === slide.id);
      if (idx < 0 || idx >= sorted.length - 1) return;
      const reordered = [...sorted];
      const next = reordered[idx + 1];
      reordered[idx + 1] = { ...slide, sort_order: next.sort_order };
      reordered[idx] = { ...next, sort_order: slide.sort_order };
      crud.reorderItems(
        reordered.map((s, i) => ({ ...s, sort_order: i + 1 })),
      );
    },
    [crud],
  );

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return;
    crud.deleteItem(deleteTarget.id);
    setDeleteTarget(null);
  }, [deleteTarget, crud]);

  const sortedItems = [...crud.items].sort(
    (a, b) => a.sort_order - b.sort_order,
  );

  const formData: HeroSlideFormData = editingSlide
    ? {
        title: editingSlide.title,
        subtitle: editingSlide.subtitle ?? "",
        image_url: editingSlide.image_url ?? "",
        cta_text: editingSlide.cta_text ?? "",
        cta_link: editingSlide.cta_link ?? "",
        sort_order: editingSlide.sort_order,
        active: editingSlide.active,
      }
    : { ...EMPTY_SLIDE_FORM, sort_order: sortedItems.length + 1 };

  const columns: readonly ColumnDef<HeroSlide>[] = [
    {
      id: "image",
      header: "Image",
      cell: (row) =>
        row.image_url ? (
          <img
            src={row.image_url}
            alt={row.title}
            className="h-10 w-16 rounded-md border border-stone-200 object-cover"
          />
        ) : (
          <div className="flex h-10 w-16 items-center justify-center rounded-md border border-dashed border-stone-200 bg-stone-50">
            <ImageIcon className="h-4 w-4 text-stone-300" />
          </div>
        ),
    },
    {
      id: "title",
      header: "Titre",
      cell: (row) => (
        <span className="font-medium text-stone-900">{row.title}</span>
      ),
    },
    {
      id: "subtitle",
      header: "Sous-titre",
      cell: (row) => (
        <span className="text-sm text-stone-500">
          {row.subtitle ?? "\u2014"}
        </span>
      ),
    },
    {
      id: "cta_text",
      header: "CTA",
      cell: (row) => (
        <span className="text-sm text-stone-500">
          {row.cta_text ?? "\u2014"}
        </span>
      ),
    },
    {
      id: "sort_order",
      header: "Ordre",
      cell: (row) => (
        <div className="flex items-center gap-1">
          <span className="mr-2 text-xs font-medium text-stone-600">
            {row.sort_order}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleMoveUp(row);
            }}
            className="rounded p-0.5 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
            aria-label="Monter"
          >
            <ArrowUp className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleMoveDown(row);
            }}
            className="rounded p-0.5 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
            aria-label="Descendre"
          >
            <ArrowDown className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
    },
    {
      id: "active",
      header: "Actif",
      cell: (row) => (
        <Switch
          checked={row.active}
          onCheckedChange={() => handleToggleActive(row)}
          aria-label={row.active ? "D\u00E9sactiver" : "Activer"}
        />
      ),
    },
    {
      id: "actions",
      header: "",
      cell: (row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => openEditModal(row)}
            className="rounded-md p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
            aria-label="Modifier"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={() => setDeleteTarget(row)}
            className="rounded-md p-1.5 text-stone-400 hover:bg-red-50 hover:text-red-500"
            aria-label="Supprimer"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {crud.isOffline && <OfflineBadge />}
        </div>
        <Button onClick={openCreateModal} className="gap-1.5">
          <Plus className="h-4 w-4" />
          Ajouter une slide
        </Button>
      </div>

      {sortedItems.length === 0 && !crud.isLoading ? (
        <EmptyState
          icon={<SlidersHorizontal className="h-6 w-6" />}
          title="Aucune slide"
          description="Ajoutez des slides hero pour votre page d'accueil."
          action={{ label: "Ajouter une slide", onClick: openCreateModal }}
        />
      ) : (
        <AdminDataTable
          columns={columns}
          data={sortedItems}
          isLoading={crud.isLoading}
          emptyMessage="Aucune slide trouvée."
        />
      )}

      <HeroSlideFormModal
        open={modalOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        initialData={formData}
        isSubmitting={crud.isCreating || crud.isUpdating}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Supprimer la slide"
        message={`Voulez-vous vraiment supprimer la slide \u00AB ${deleteTarget?.title ?? ""} \u00BB ? Cette action est irr\u00E9versible.`}
        isLoading={crud.isDeleting}
      />
    </div>
  );
}
